import { Auth, API } from 'aws-amplify';
import React, { useContext, useState } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { flatten } from '../utils/flatten';
import Button from './shared/Button';
import { ReactComponent as LogOutIcon } from '../image/log-out-icon.svg';
import Modal from './shared/Modal';
import { ProtectedRouteContext, ShipyardContext } from './Context';
import NavBar from './NavBar';
import Page from './Page';
import PostAuth from './PostAuth';
import Timer from './shared/Timer';

import './ProtectedRoute.scss';
import { userTypes } from '../utils/enums';
import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material';
import RenderIf from './renderIf/RenderIf';

function ProtectedRoute({ component: Component, restrictedTo = Array.from(userTypes), ...restOfProps }) {
    const [t] = useTranslation();
    let [isAuthenticated, setAuthenticated] = React.useState(false);
    let [isAuthLoading, setIsAuthLoading] = React.useState(true);
    let [isAuthorized, setAuthorized] = useState(false);
    const history = useHistory();
    const { user, userSettings, updateUser, updateUserSettings, resetValue, setListSettings } = useContext(ShipyardContext);

    const [logout, setLogout] = useState(false);
    const [sessionExpired, setSessionExpired] = useState(false);
    const [isBlur, setIsBlur] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [sideModal, setSideModal] = useState(false);
    const [ajaxErrorMessage] = useState(t('General.AjaxError'));
    const [warning, setWarning] = useState(null);

    const stayLoggedIn = () => {
        setLogout(false);
    };

    const close = () => {
        window.sessionStorage.clear();
        Auth.signOut();
        updateUserSettings((prev) => {
            let clientSettings = {
                ClientOrgId: prev.ClientOrgId,
                ClientHeaderImage: prev.ClientHeaderImage,
                ClientHeaderText: prev.ClientHeaderText,
                ClientName: prev.ClientName,
            };
            return { ...resetValue, ...clientSettings };
        });
        history.push('/signin');
    };

    const autoLogout = (event) => {
        if (isIdle()) {
            setListSettings({
                target: null,
                reducerState: null,
                useCache: false,
            });
            toast.info(t('General.AutoLogout'));
            close();
        }
    };

    const displayModal = (boolBlur, objModal, _sideModal = false, titleText = null) => {
        setIsBlur(boolBlur);
        setModalContent(objModal);
        setSideModal(_sideModal);
    };

    const modalOnIdle = (event) => {
        setLogout(popupTimer.isIdle());
    };

    const popupTimer = useIdleTimer({
        timeout: 1000 * 60 * 8,
        onIdle: modalOnIdle,
        debounce: 500,
    });

    const { isIdle, getRemainingTime: getTimeTilLogout } = useIdleTimer({
        timeout: 1000 * 60 * 10,
        onIdle: autoLogout,
        debounce: 500,
    });

    React.useEffect(() => {
        (async () => {
            var currentAuthUser = null;
            try {
                currentAuthUser = await Auth.currentAuthenticatedUser();
            } catch {
                //user is not authenticated
            }

            if (currentAuthUser !== null) {
                updateUser(currentAuthUser);
                setAuthenticated(true);
            } else {
                updateUser(null);
                setAuthenticated(false);
                setIsAuthLoading(false);
            }
        })();
    }, [updateUser]);

    React.useEffect(() => {
        (async () => {
            if (isAuthenticated && userSettings.Retrieved === false) {
                try {
                    let userData = await Auth.currentUserInfo();

                    let response = await API.get('ShipyardAPI', '/usersettings');
                    let newUserSettingsState = {
                        UserName: userData.attributes.email,
                        PhoneNumber: userData.attributes.phone_number,
                        Retrieved: true,
                    };
                    newUserSettingsState = Object.assign(newUserSettingsState, flatten(response));
                    setAuthorized(restrictedTo.includes(newUserSettingsState.UserType));
                    updateUserSettings((prev) => {
                        return { ...prev, ...newUserSettingsState };
                    });
                    setIsAuthLoading(false);
                } catch (e) {
                    if (e !== 'No current user') toast.error(ajaxErrorMessage);
                    setSessionExpired(true);
                }
            } else if (isAuthenticated && userSettings.Retrieved) {
                setAuthorized(restrictedTo.includes(userSettings.UserType));
                setIsAuthLoading(false);
            }
        })();
    }, [isAuthenticated, ajaxErrorMessage, userSettings.Retrieved, userSettings.UserType, updateUserSettings, restrictedTo]);

    React.useEffect(() => {
        setIsBlur(logout || sessionExpired);
    }, [logout, sessionExpired]);

    return (
        <Route
            {...restOfProps}
            render={(props) =>
                isAuthLoading ? null : isAuthenticated && isAuthorized ? (
                    <Page className="page">
                        <ProtectedRouteContext.Provider
                            value={{ displayModal, user, userSettings, updateUserSettings, warning, setWarning }}
                        >
                            <div className={isBlur ? 'blur' : ''}>
                                <NavBar />
                            </div>
                            <div className={isBlur ? 'blur view' : 'view'}>
                                <PostAuth>
                                    <Component {...props} />
                                </PostAuth>
                            </div>
                            {logout || sessionExpired ? (
                                <Dialog
                                    open={true}
                                    sx={{
                                        '& *': {
                                            fontFamily:
                                                '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif',
                                        },
                                    }}
                                >
                                    <DialogTitle>
                                        <RenderIf isTrue={logout}>Your session is about to expire.</RenderIf>
                                        <RenderIf isTrue={sessionExpired}>Your session has expired.</RenderIf>
                                    </DialogTitle>
                                    <DialogContent>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexFlow: 'column',
                                                alignItems: 'center',
                                                color: '#586874',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    display: 'flex',
                                                    fontSize: 'calc(16rem/16)',
                                                    '& path': {
                                                        fill: '#D24444',
                                                    },
                                                }}
                                            >
                                                <LogOutIcon style={{ marginRight: 'calc(8rem/16)' }} />
                                                <span>
                                                    <RenderIf isTrue={logout}>You will be logged out in:</RenderIf>
                                                    <RenderIf isTrue={sessionExpired}>
                                                        You will be redirected to the signin page in:
                                                    </RenderIf>
                                                </span>
                                            </Typography>
                                            <Timer
                                                timerCallback={getTimeTilLogout}
                                                sx={{
                                                    '& h1': {
                                                        margin: '0 0 calc(16rem/16) 0',
                                                    },
                                                }}
                                            />
                                            <Typography
                                                sx={{
                                                    fontSize: 'calc(19rem/16)',
                                                }}
                                            >
                                                <RenderIf isTrue={logout}>
                                                    If you wish to continue working, click "Stay Logged In" or click "Log out" to end your
                                                    session now.
                                                </RenderIf>
                                                <RenderIf isTrue={sessionExpired}>
                                                    Click the "Log out" button to be redirected now.
                                                </RenderIf>
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Button
                                                type="negative"
                                                onClick={close}
                                            >
                                                Log out
                                            </Button>
                                            <Button onClick={stayLoggedIn}>Stay logged in</Button>
                                        </Box>
                                    </DialogContent>
                                </Dialog>
                            ) : isBlur && modalContent && sideModal ? (
                                <Modal classes={['side-modal']}>{modalContent}</Modal>
                            ) : (
                                modalContent && <>{modalContent}</>
                            )}
                        </ProtectedRouteContext.Provider>
                    </Page>
                ) : (
                    <Redirect to="/signin" />
                )
            }
        />
    );
}

export default ProtectedRoute;

